import createPersistedReducer from '@/redux/lib/createPersistedReducer';
import mergeByProp from '../../lib/mergeByProp';
import { cleanObject } from '@shared/utils/cleanObject.js';
import { parseJson } from '@shared/utils/parseJson.js';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { pageSize } from '@/lib/constants/jobs';

const initialState = {
  jobManagerState: undefined,
  jobs: [],
  nextJobs: [],
  jobMemory: {},
  jobConfig: {},
  jobLogs: {},
  jobLogOutputs: {},
  recentJobLogs: [],
  userJobAlerts: [],
  jobErrors: undefined
};

const persistConfig = {
  key: 'jobsReducer',
  whitelist: [],
  transforms: []
};

export default createPersistedReducer(
  initialState,
  {
    GET_JOBS_SUCCESS: (state, payload) => ({
      ...state,
      jobs: payload.data
    }),

    GET_RECENT_JOB_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      recentJobLogs: payload.data
    }),

    GET_JOB_LOGS_BY_JOB_ID_SUCCESS: (state, payload) => {
      const jobId = payload.data.logs[0]?.jobId;
      if (!jobId) {
        return state;
      }

      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          [jobId]: {
            logs: payload.data.logs.sort(sortByProp('startedOn', 'desc')).map((log) => {
              // Sometimes the GET_JOB_LOG_BY_ID_SUCCESS reducer will be called before this one
              // Since it includes the payload.updates and this response doesn't, we need to merge them
              // so it doesn't get lost
              const existingUpdates = state.jobLogs[jobId]?.logs.find((l) => l.id === log.id)?.payload?.updates;
              if (!existingUpdates) {
                return log;
              }

              const newLog = { ...log };
              newLog.payload.updates = existingUpdates;
              return newLog;
            }),
            total: payload.data.total ?? 0,
            page: state.jobLogs[jobId]?.page ?? 1,
            active: state.jobLogs[jobId]?.active
          }
        }
      };
    },
    GET_ACTIVE_JOB_LOGS_BY_JOB_ID_SUCCESS: (state, payload) => {
      const jobId = payload.data.jobId;
      if (!state.jobLogs[jobId]) {
        return state;
      }

      const newState = {
        ...state
      };
      const logs = payload.data.logs;

      const hasRunningJob = logs?.find((n) => !n?.finishedOn || [10, 0].includes(n?.statusId));
      if (hasRunningJob) {
        newState.jobLogs[jobId].active = payload.data.logs[0];
      } else {
        newState.jobLogs[jobId].active = null;
      }
      if (state.jobLogs[jobId].page === 1) {
        newState.jobLogs[jobId].logs = mergeByProp('id', state.jobLogs[jobId].logs, payload.data.logs, (prev, curr) => {
          return prev.payload && !curr.payload ? prev : curr;
        }).sort(sortByProp('startedOn', 'desc'));
      }

      return newState;
    },
    GET_JOB_LOG_BY_ID_SUCCESS: (state, payload) => {
      const jobId = payload.data[0]?.jobId;
      if (!jobId || !state.jobLogs[jobId]) {
        return state;
      }

      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          [jobId]: {
            ...state.jobLogs[jobId],
            logs: mergeByProp('id', state?.jobLogs[jobId].logs, payload.data, (prev, curr) => {
              return prev.payload && !curr.payload ? prev : curr;
            }).sort(sortByProp('startedOn', 'desc'))
          }
        }
      };
    },
    setJobLogsAtivePageByJobId: (state, payload) => {
      const currentPage = state.jobLogs[payload.jobId]?.page ?? 1;
      const totalPages = Math.ceil(state.jobLogs[payload.jobId]?.total / pageSize);
      const action = payload.action; // 'increment' or 'decrement'
      let newPage = currentPage;
      if (action === 'increment') {
        if (currentPage === totalPages) {
          return state;
        }
        newPage = currentPage + 1;
      } else if (action === 'decrement') {
        if (currentPage === 1) {
          return state;
        }
        newPage = currentPage - 1;
      } else if (action === 'set') {
        newPage = payload.page;
      } else {
        return state;
      }

      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          [payload.jobId]: {
            ...state.jobLogs[payload.jobId],
            page: newPage
          }
        }
      };
    },
    GET_JOB_MEMORY_SUCCESS: (state, payload) => ({
      ...state,
      jobMemory: {
        ...state.jobMemory,
        [payload?.jobId]: payload?.memory
      }
    }),

    DELETE_JOB_MEMORY_SUCCESS: (state, payload) => ({
      ...state,
      jobMemory: {
        ...state.jobMemory,
        [payload?.jobId]: payload?.memory
      }
    }),

    GET_NEXT_JOBS_SUCCESS: (state, payload) => ({
      ...state,
      nextJobs: payload.data || []
    }),

    GET_JOB_CONFIG_SUCCESS: (state, payload) => ({
      ...state,
      jobConfig: {
        ...state.jobConfig,
        [payload.data?.jobId]: payload.data || []
      }
    }),

    GET_JOB_LOG_OUTPUT_SUCCESS: (state, payload) => ({
      ...state,
      jobLogOutputs: {
        ...state.jobLogOutputs,
        [payload?.data?.jobLogId]: parseJson(payload.data.output) || payload.data.output
      }
    }),

    PUT_JOB_SUCCESS: (state, payload) => ({
      ...state,
      jobs: mergeByProp('id', [...state.jobs], [...payload.data], (a, b) => ({ ...a, ...cleanObject(b) }))
    }),

    GET_JOB_MANAGER_STATE_SUCCESS: (state, payload) => ({
      ...state,
      jobManagerState: payload.data
    }),

    GET_ERRORING_JOBS_SUCCESS: (state, payload) => ({
      ...state,
      jobErrors: payload.data
    }),

    PUT_USER_JOB_ALERT_SUCCESS: (state, payload) => ({
      ...state,
      userJobAlerts: payload.data
    }),

    DELETE_JOB_LOG_SUCCESS: (state, payload) => ({
      ...state,
      jobLogs: {
        ...state.jobLogs,
        [payload?.meta?.jobId]: {
          ...state.jobLogs[payload?.meta?.jobId],
          logs: state.jobLogs[payload?.meta?.jobId]?.logs.filter((n) => n.id !== payload?.meta?.jobLogId)
        }
      }
    })
  },
  persistConfig
);
